import { servicesNav } from './waq/servicesNav';

/**
 * The header menu.
 *
 * Grouped rather than flat. The flat list ran out of room: with Services added
 * it occupied 1054px of the 1064px available, and Industries and Case Studies
 * needed roughly 200px more. Collapsing About/Blog/Contact into one disclosure
 * and dropping Home — the logo already goes there — buys that room back and
 * leaves headroom for the next few pages.
 *
 * To add a page: append to a group's `items`, or add a `NavLink` here. Nothing
 * in Layout is keyed to a specific label.
 */
export type NavLink = {
  label: string;
  href: string;
  /** One line of orientation, shown under the label inside a dropdown. */
  note?: string;
};

export type NavGroup = {
  label: string;
  items: NavLink[];
};

export type NavEntry = NavLink | NavGroup;

export const isGroup = (entry: NavEntry): entry is NavGroup => 'items' in entry;

const servicesGroup: NavGroup = {
  label: 'Services',
  items: servicesNav,
};

const industriesGroup: NavGroup = {
  label: 'Industries',
  items: [
    { label: 'Fintech & Financial Services', href: '/industries/fintech', note: 'Payments, trading and regulated money movement' },
    { label: 'Healthcare & HealthTech', href: '/industries/healthcare', note: 'Patient safety and clinical data' },
    { label: 'Pharma', href: '/industries/pharma', note: 'GxP, validation and audit trails' },
    { label: 'E-commerce & SaaS', href: '/industries/ecommerce', note: 'Checkout, scale and release velocity' },
    { label: 'Communications', href: '/industries/communications', note: 'Networks, messaging and real-time delivery' },
  ],
};

/**
 * The two audience landing pages, grouped rather than left at top level.
 *
 * Flat they cost 254px and left the row with 14px to spare, which defeats the
 * point of grouping. They are NOT dropped: the header CTAs go to the
 * application forms (/apply-qa-engineer, /hire-qa-talent), not to these pages,
 * so removing them would leave both entry points reachable only from the footer.
 *
 * `Who We Help` is the one label here not already in use on the site. The
 * footer names the same two audiences "For Professionals" and "For Employers",
 * but those are two headings and this needs one.
 */
const audienceGroup: NavGroup = {
  label: 'Who We Help',
  items: [
    { label: 'For QA Engineers', href: '/for-qa-engineers', note: 'Remote roles with global companies' },
    { label: 'Hire QA Engineers', href: '/for-employers', note: 'No recruitment fee' },
  ],
};

export const headerNav: NavEntry[] = [
  servicesGroup,
  industriesGroup,
  /**
   * A plain link, not a disclosure. The panel listed the three studies, and
   * /case-studies already lists them — so the dropdown duplicated the page it
   * was named after. The studies stay reachable from there.
   */
  { label: 'Case Studies', href: '/case-studies' },
  audienceGroup,
  {
    label: 'Company',
    items: [
      { label: 'About Us', href: '/about', note: 'Who we are and how we work' },
      { label: 'Blog', href: '/blog', note: 'Engineering notes and QA practice' },
      { label: 'Contact', href: '/contact', note: 'Talk to an engineer' },
    ],
  },
];
